import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Award, Users, Briefcase, Target, CheckCircle2 } from 'lucide-react';

const BestExpertsSection = ({ onOpenContactForm }) => {
  return (
    <section className="relative py-20 bg-gradient-to-br from-gray-900 via-[#1a1a2e] to-gray-900 overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full opacity-5 blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#FF9900] rounded-full opacity-5 blur-3xl"></div>
      
      {/* Diagonal Stripe */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-[#FF9900]/5 to-transparent" style={{ clipPath: 'polygon(0 20%, 100% 0, 100% 80%, 0 100%)' }}></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-stretch">
          {/* Image on Left - Full Height with Curved Border */}
          <div className="order-2 lg:order-1">
            <div className="relative h-full group">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <img
                src="/section3.png"
                alt="Best trademark registration experts"
                className="relative w-full h-full object-cover rounded-3xl shadow-2xl border-2 border-white/10"
              />
            </div>
          </div>

          {/* Content on Right - Enhanced */}
          <div className="order-1 lg:order-2 space-y-6 flex flex-col justify-between">
            <div>
              <div className="inline-block bg-gradient-to-r from-[#FF9900]/20 to-orange-600/20 border border-[#FF9900]/30 text-[#FF9900] px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Industry Leaders
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                Expert Trademark Lawyers | US Trademark Registration Services
              </h2>

              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Our trademark attorneys provide specialized trademark legal services with online trademark registration, USPTO trademark filing, and federal trademark application support. We are the leading trademark lawyers offering comprehensive trademark registration services, copyright registration, LLC formation, and Amazon Brand Registry enrollment across the United States.
              </p>

              {/* Expertise Cards */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <Award className="w-8 h-8 text-[#FF9900] mb-2" />
                  <h4 className="text-white font-bold mb-1">Certified Experts</h4>
                  <p className="text-gray-400 text-sm">USPTO specialists</p>
                </div>
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <Users className="w-8 h-8 text-[#FF9900] mb-2" />
                  <h4 className="text-white font-bold mb-1">5,000+ Clients</h4>
                  <p className="text-gray-400 text-sm">Nationwide trust</p>
                </div>
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <Briefcase className="w-8 h-8 text-[#FF9900] mb-2" />
                  <h4 className="text-white font-bold mb-1">All Industries</h4>
                  <p className="text-gray-400 text-sm">Complete coverage</p>
                </div>
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <Target className="w-8 h-8 text-[#FF9900] mb-2" />
                  <h4 className="text-white font-bold mb-1">Strategic Counsel</h4>
                  <p className="text-gray-400 text-sm">Expert guidance</p>
                </div>
              </div>

              <p className="text-lg text-gray-300 leading-relaxed mb-4">
                Our trademark registration services cover all USPTO trademark classes and industries nationwide. We provide trademark search services, brand name search, business name search, trademark attorney consultation, and complete trademark filing support. Register a trademark with expert trademark lawyer guidance, comprehensive trademark database search, and professional trademark application assistance.
              </p>

              {/* Key Features List */}
              <div className="space-y-3 mb-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-[#FF9900] mt-1 flex-shrink-0" />
                  <p className="text-gray-300">Dedicated case manager for every client</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-[#FF9900] mt-1 flex-shrink-0" />
                  <p className="text-gray-300">Real-time updates throughout the process</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-[#FF9900] mt-1 flex-shrink-0" />
                  <p className="text-gray-300">Advanced search tools and proprietary databases</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-[#FF9900] mt-1 flex-shrink-0" />
                  <p className="text-gray-300">Proactive brand monitoring and protection</p>
                </div>
              </div>

              {/* Trust Banner */}
              <div className="bg-gradient-to-r from-[#FF9900]/20 to-orange-600/20 border border-[#FF9900]/30 rounded-xl p-4">
                <h4 className="text-[#FF9900] font-bold mb-2">Comprehensive Trademark & Business Services</h4>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Professional trademark registration services with online trademark filing, federal trademark application support, copyright registration services, company formation, LLC registration, start LLC services, and Amazon seller brand registry enrollment. Our trademark lawyers handle trademark search, USPTO filing, brand protection, and complete trademark legal services for businesses nationwide.
                </p>
              </div>
            </div>

            <div>
              <Button
                onClick={onOpenContactForm}
                size="lg"
                className="bg-gradient-to-r from-[#FF9900] to-orange-600 hover:from-[#FF9900]/90 hover:to-orange-600/90 text-black px-8 py-6 text-lg mt-4 font-semibold shadow-lg"
              >
                Register My Trademark
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BestExpertsSection;